import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom"; // Assuming you use react-router
import { _get } from "../services/apiClient";
import { apiUrls } from "../config/apiUrls.const";
import Slider from "./components/Slider";
import MapInput from "./components/MapInput";
import { BookingForm, BookingFormRef } from "./components/BookingForm";

type BookingMode = "scheduled" | "instant";

export default function ServiceDetails() {
  const bookingFormRef = useRef<BookingFormRef>(null);
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("id");

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const defaultImage = "no-image-found.jpg";

  useEffect(() => {
    async function fetchData() {
      if (!serviceId) {
        setError("No Service ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Ensure your API endpoint can handle the ID (e.g., /services/3)
        const response = await _get(`${apiUrls?.serviceItemDetails}${serviceId}`);
        console.log(response.data?.data)
        setData(response.data?.data);
      } catch (err: any) {
        setError(err.message || "Failed to load service details");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [serviceId]);

  if (loading) return <div className="p-5 text-center">Loading service details...</div>;
  if (error) return <div className="alert alert-danger m-5">{error}</div>;
  if (!data) return <div className="p-5 text-center">No data found.</div>;

  return (
    <>
      <h1 className="page-header">{data.title || "Service Details"}</h1>
      <hr className="section-devider" />

      <div className="row mb-3">
        <div className="col-md-6">
          {/* Case A: Multiple media gallery elements exist -> Trigger Slider */}
          {data?.images && data.images.length > 1 ? (
            <Slider images={data.images.map((img: any) => img.image_url)} />
          ) : 
          
          /* Case B: Exactly one image asset is bound -> Render directly */
          data?.images && data.images.length === 1 ? (
            <img
              src={data.images[0]?.image_url || defaultImage}
              alt={data.title}
              className="img-fluid rounded shadow-sm"
              style={{ width: '100%', height: '400px', objectFit: 'cover' }}
              onError={(e) => {
                // Fallback gracefully if port 2828 is unreachable
                (e.target as HTMLImageElement).src = defaultImage;
              }}
            />
          ) : 
          
          /* Case C: Fallback placeholder rule */
          (
            <img
              src={defaultImage}
              alt={data.title}
              className="img-fluid rounded"
              style={{ width: '100%', height: '400px', objectFit: 'cover' }}
            />
          )}
        </div>
        <div className="col-md-6">
          <div className="mb-2">
            <span className="title-info"><span className="pointer text-link fs-130 fw-500">{data.title}</span></span>
          </div>
          <div className="fw-400">
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="mb-1 fs-120">
                  <span className="text-dark">Duration:</span> <span className="text-secondary">{data.duration || "N/A"}</span>
                </div>
                <div className="mb-1 fs-120">
                  <span className="text-dark">Price:</span> <span className="text-secondary">{data.price} {data.currency || "BDT"}</span>
                </div>
                <div className="mb-1 fs-120">
                  <span className="text-dark">Address</span> <br />
                  <span className="text-secondary">
                    <b className="text-light-blue">{data?.business?.name}</b>, <br />
                    {data.business.address}
                  </span>
                </div>
                {/* <div className="mb-1 fs-120">
                  <span className="text-dark">Distance:</span> <span className="text-secondary">{data.distance || "0.0 km"}</span>
                </div> */}
              </div>
              <div className="col-md-6">
                <MapInput
                  mode="display"
                  mapHeight="240px"
                  lat={data.latitude || 23.8103}
                  lon={data.longitude || 90.4125}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <h5 className="mb-3">Service Description</h5>
          <div className="service-description">
            {data.description || "No description available for this service."}
          </div>
        </div>
        <div className="col-md-6 d-flex align-items-end justify-content-end">
          <div className="d-flex flex-column gap-2 w-100" style={{ maxWidth: "320px" }}>
        <button
          className="btn btn-outline-primary btn-lg w-100"
          onClick={() => bookingFormRef.current?.open(data, "scheduled")}
        >
          📅 Book Now
        </button>
        <button
          className="btn btn-outline-primary btn-lg w-100"
          onClick={() => bookingFormRef.current?.open(data, "instant")}
        >
          🔥 Instant Service
        </button>
      </div>
        </div>
      </div>
      {/* Booking Modal */}
      <BookingForm ref={bookingFormRef} onSuccess={() => /* Refresh data if needed */ { }} />
    </>
  );
}